<!-- Notification Item using Flowbite design -->
<div class="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700"
  [ngClass]="{
    'bg-blue-50': !notification.isRead,
    'dark:bg-blue-900/20': !notification.isRead, 
    'cursor-pointer': clickable
  }"
  (click)="onClick()">

  <div class="flex items-start gap-3">
    <!-- Avatar -->
    <div class="flex-shrink-0">
      <img 
        *ngIf="notification.avatar" 
        [src]="notification.avatar" 
        class="w-10 h-10 rounded-full border-2 border-gray-200 dark:border-gray-700"
        alt="avatar">
        
      <!-- Default Avatar -->
      <div *ngIf="!notification.avatar" 
        class="relative w-10 h-10 overflow-hidden bg-gray-100 rounded-full dark:bg-gray-600 flex items-center justify-center border-2 border-gray-200 dark:border-gray-700">
        <svg class="absolute w-12 h-12 text-gray-400 -left-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"></path>
        </svg>
      </div>
    </div>
    
    <!-- Content Column -->
    <div class="flex flex-col w-full">
      <!-- Header with name and time -->
      <div class="flex items-center justify-between mb-1">
        <div class="flex items-center">
          <span class="text-sm font-semibold text-gray-900 dark:text-white mr-2">
            {{ notification.to.firstName || 'User' }}
          </span>
          <span class="text-xs font-normal text-gray-500 dark:text-gray-400">
            {{ formatRelativeTime(notification.createdAt) }}
          </span>
        </div>
        
        <!-- Unread indicator -->
        <span *ngIf="!notification.isRead" class="inline-flex h-2 w-2 rounded-full bg-blue-500"></span>
      </div>
      
      <!-- Message Content -->
      <div class="px-3 py-2 bg-gray-100 rounded-lg dark:bg-gray-700 shadow-sm">
        <!-- Notification subject -->
        <div *ngIf="notification.subject" class="text-sm font-bold text-gray-900 dark:text-white mb-1">
          {{ notification.subject }}
        </div>
        
        <!-- Regular text body display -->
        <p *ngIf="!isJsonString(notification.body)" class="text-sm font-normal text-gray-900 dark:text-white">
          {{ notification.body }}
        </p>
        
        <!-- JSON content display with formatting -->
        <div *ngIf="isJsonString(notification.body)" class="text-xs overflow-auto font-mono bg-gray-800 text-white p-2 rounded-md dark:bg-gray-900 max-h-40">
          <pre>{{ formatJson(notification.body) }}</pre>
        </div>
      </div>
      
      <!-- Action Buttons - Enhanced for Accept/Deny pattern -->
      <div *ngIf="notification.primaryAction || notification.secondaryAction" 
        class="flex flex-wrap gap-2 mt-3 justify-end" 
        [ngClass]="{'flex-col sm:flex-row': isMobile, 'w-full': isMobile}">
        
        <!-- Primary Action Button (Accept) -->
        <button 
          *ngIf="notification.primaryAction" 
          (click)="onPrimaryActionClick($event)"
          class="px-3 py-1.5 text-xs font-medium text-center text-white bg-green-600 rounded-lg hover:bg-green-700 focus:ring-4 focus:outline-none focus:ring-green-300 dark:bg-green-500 dark:hover:bg-green-600 dark:focus:ring-green-700 transition-colors duration-200 ease-in-out"
          [ngClass]="{'w-full sm:w-auto': isMobile}"
          [disabled]="loadingPrimary">
          <div class="flex items-center justify-center gap-1">
            <svg *ngIf="loadingPrimary" class="animate-spin w-3 h-3" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
            <svg *ngIf="!loadingPrimary" class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14m-7 7V5"/>
            </svg>
            <span *ngIf="!loadingPrimary">{{ notification.primaryAction.label || 'Accept' }}</span>
            <span *ngIf="loadingPrimary">Loading...</span>
          </div>
        </button>
        
        <!-- Secondary Action Button (Deny) -->
        <button 
          *ngIf="notification.secondaryAction" 
          (click)="onSecondaryActionClick($event)"
          class="px-3 py-1.5 text-xs font-medium text-center text-white bg-red-600 rounded-lg hover:bg-red-700 focus:ring-4 focus:outline-none focus:ring-red-300 dark:bg-red-500 dark:hover:bg-red-600 dark:focus:ring-red-700 transition-colors duration-200 ease-in-out"
          [ngClass]="{'w-full sm:w-auto': isMobile}"
          [disabled]="loadingSecondary">
          <div class="flex items-center justify-center gap-1">
            <svg *ngIf="loadingSecondary" class="animate-spin w-3 h-3" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
            <svg *ngIf="!loadingSecondary" class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18 18 6m0 12L6 6"/>
            </svg>
            <span *ngIf="!loadingSecondary">{{ notification.secondaryAction.label || 'Deny' }}</span>
            <span *ngIf="loadingSecondary">Loading...</span>
          </div>
        </button>
      </div>
      
      <!-- Notification Control Buttons -->
      <div class="flex items-center mt-2 space-x-4 rtl:space-x-reverse text-xs border-t border-gray-100 dark:border-gray-700 pt-2">
        <button 
          type="button" 
          (click)="onToggleReadStatus($event)"
          class="flex items-center text-xs text-gray-500 hover:text-blue-600 hover:underline dark:text-gray-400 dark:hover:text-blue-400 transition-colors duration-200 ease-in-out">
          <svg class="w-3.5 h-3.5 mr-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M1 12c0-4.4 3.6-8 8-8a7.9 7.9 0 0 1 7.5 5.5h5.5v5h-2v2.5h-3.5v-7.5a5.4 5.4 0 0 0-5-5.5C7.1 4 4 7.1 4 11c0 3.9 3.1 7 7 7a6.3 6.3 0 0 0 5.5-3.5h3a9 9 0 0 1-7.5 5.5c-4.4 0-8-3.6-8-8Z"/>
          </svg>
          <span>{{ notification.isRead ? 'Mark as unread' : 'Mark as read' }}</span>
        </button>
        <button 
          type="button" 
          (click)="onArchive($event)"
          class="flex items-center text-xs text-gray-500 hover:text-blue-600 hover:underline dark:text-gray-400 dark:hover:text-blue-400 transition-colors duration-200 ease-in-out">
          <svg class="w-3.5 h-3.5 mr-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z"/>
          </svg>
          <span>Archive</span>
        </button>
      </div>
    </div>
  </div>
</div> 